import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, Globe, Heart, Home, Mail, MapPin, Phone, Search, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const NotFound = () => {
    const quickLinks = [
        { name: 'Home', path: '/', icon: <Home className="h-5 w-5" /> },
        { name: 'About Us', path: '/about', icon: <Users className="h-5 w-5" /> },
        { name: 'Our Work', path: '/our-work', icon: <Heart className="h-5 w-5" /> },
        { name: 'Get Involved', path: '/get-involved', icon: <Globe className="h-5 w-5" /> },
        { name: 'Contact', path: '/contact', icon: <Phone className="h-5 w-5" /> },
    ];

    const contactInfo = [
        {
            icon: <Phone className="h-5 w-5" />,
            label: 'Call Us',
            value: '+211 912 446 7697',
            action: 'tel:+2119124467697',
        },
        {
            icon: <Mail className="h-5 w-5" />,
            label: 'Email Us',
            value: 'infoacdha@gmail.com',
            action: 'mailto:infoacdha@gmail.com',
        },
        {
            icon: <MapPin className="h-5 w-5" />,
            label: 'Visit Us',
            value: 'Hai, Malakia Police Building, Juba',
            action: '#',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
            {/* Hero Section */}
            <section className="relative px-4 py-20 md:px-8 lg:px-16">
                <div className="mx-auto max-w-4xl text-center">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        {/* 404 Icon */}
                        <div className="mb-8">
                            <motion.div
                                className="mx-auto mb-6 flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-green-600 to-green-700"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <AlertTriangle className="h-16 w-16 text-white" />
                            </motion.div>
                        </div>

                        {/* Main Content */}
                        <motion.h1
                            className="mb-4 text-6xl font-bold text-green-800 md:text-8xl"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            404
                        </motion.h1>

                        <motion.h2
                            className="mb-6 text-3xl font-bold text-green-800 md:text-4xl"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            Page Not Found
                        </motion.h2>

                        <motion.p
                            className="mx-auto mb-8 max-w-2xl text-xl text-gray-600"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        >
                            Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL.
                        </motion.p>

                        {/* Action Buttons */}
                        <motion.div
                            className="mb-12 flex flex-col justify-center gap-4 sm:flex-row"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <Link
                                to="/"
                                className="inline-flex transform items-center justify-center rounded-lg bg-green-800 px-8 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-green-700"
                            >
                                <Home className="mr-2 h-5 w-5" />
                                Go Home
                            </Link>
                            <button
                                onClick={() => window.history.back()}
                                className="inline-flex transform items-center justify-center rounded-lg border-2 border-green-800 bg-transparent px-8 py-4 font-semibold text-green-800 transition-all duration-300 hover:scale-105 hover:bg-green-100"
                            >
                                <ArrowLeft className="mr-2 h-5 w-5" />
                                Go Back
                            </button>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Quick Navigation */}
            <section className="bg-white px-4 py-16 md:px-8 lg:px-16">
                <div className="mx-auto max-w-6xl">
                    <motion.div
                        className="mb-12 text-center"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="mb-4 text-3xl font-bold text-green-800">Find What You're Looking For</h3>
                        <p className="mx-auto max-w-2xl text-lg text-gray-600">
                            Explore our website to discover how ACDHA is making a difference in South Sudan.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
                        {quickLinks.map((link, index) => (
                            <motion.div
                                key={link.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <Link
                                    to={link.path}
                                    className="block transform rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-6 transition-all duration-300 hover:scale-105 hover:from-green-100 hover:to-green-200"
                                >
                                    <div className="mb-4 text-green-700">{link.icon}</div>
                                    <h4 className="text-lg font-semibold text-green-800">{link.name}</h4>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="bg-gradient-to-br from-green-50 to-white px-4 py-16 md:px-8 lg:px-16">
                <div className="mx-auto max-w-4xl">
                    <motion.div
                        className="mb-12 text-center"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="mb-4 text-3xl font-bold text-green-800">Need Help?</h3>
                        <p className="text-lg text-gray-600">Can't find what you're looking for? Get in touch with our team.</p>
                    </motion.div>

                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        {contactInfo.map((contact, index) => (
                            <motion.div
                                key={contact.label}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="rounded-xl bg-white p-6 text-center shadow-lg transition-shadow duration-300 hover:shadow-xl"
                            >
                                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                                    <div className="text-green-700">{contact.icon}</div>
                                </div>
                                <h4 className="mb-2 text-lg font-semibold text-green-800">{contact.label}</h4>
                                <a href={contact.action} className="text-gray-600 transition-colors duration-200 hover:text-green-700">
                                    {contact.value}
                                </a>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Search Section */}
            <section className="bg-white px-4 py-16 md:px-8 lg:px-16">
                <div className="mx-auto max-w-2xl text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="mb-6 text-3xl font-bold text-green-800">Search Our Website</h3>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search for programs, news, or information..."
                                className="w-full rounded-lg border-2 border-green-200 px-6 py-4 text-lg focus:border-green-600 focus:outline-none"
                            />
                            <button className="absolute top-2 right-2 rounded-lg bg-green-800 p-3 text-white transition-colors duration-200 hover:bg-green-700">
                                <Search className="h-5 w-5" />
                            </button>
                        </div>
                        <p className="mt-4 text-gray-600">Try searching for: "volunteer", "donate", "programs", "news", or "contact"</p>
                    </motion.div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="bg-gradient-to-br from-green-800 to-green-700 px-4 py-16 md:px-8 lg:px-16">
                <div className="mx-auto max-w-4xl text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="mb-6 text-3xl font-bold text-white md:text-4xl">Join Our Mission</h3>
                        <p className="mx-auto mb-8 max-w-2xl text-xl text-green-100">
                            Even though this page wasn't found, you can still make a difference. Get involved with ACDHA and help transform lives in
                            South Sudan.
                        </p>
                        <div className="flex flex-col justify-center gap-4 sm:flex-row">
                            <Link
                                to="/get-involved"
                                className="transform rounded-lg bg-yellow-500 px-8 py-4 text-lg font-semibold text-green-800 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-yellow-400"
                            >
                                Get Involved
                            </Link>
                            <Link
                                to="/donate"
                                className="transform rounded-lg border-2 border-white bg-transparent px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-white/10"
                            >
                                Donate Now
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default NotFound;
